import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {db} from '../firebase';
import {get, onValue, ref, remove, set, update} from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import {isPackageAllowedForDevices} from '../utils/appVersions';
import {
  FiChevronDown,
  FiClock,
  FiHardDrive,
  FiLayers,
  FiPackage,
  FiPieChart,
  FiPlus,
  FiRotateCcw,
  FiSettings,
  FiTrash2,
  FiUsers,
  FiVolume2
} from 'react-icons/fi';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {Pie} from "react-chartjs-2";
import CustomDropdown from "../components/CustomDropdown";
import CustomSlider from "../components/CustomSlider";
import BulkOperationProgress from "../components/BulkOperationProgress";
import {batchUpdate, countDevicesFromPaths, FIREBASE_BATCH_LIMIT} from '../utils/batchOperations';
import {applyGroupChange, loadGroupHistory, revertGroupChange} from '../utils/groupChanges';

const DEFAULT_GROUP_SETTINGS = {
  appsToInstall:   [],
  otaUpdate:       { enabled: false, reboot: false },
  screenTimeout:   30,
  kioskMode:       false,
  volumeLevel:     50,
  brightnessLevel: 50,
  commands:        [],
};

export default function GroupsPage() {
  /* AUDIO + DISPLAY default values */
  const [defaultVolume, setDefaultVolume]     = useState(80); // 0‑100
  const [editDefaultVolume, setEditDefaultVolume]     = useState(80); // 0‑100
  const [defaultBrightness, setDefaultBrightness] = useState(70); // 0‑100
  const [screenTimeout, setScreenTimeout]     = useState(60); // seconds







  /* APP MANAGEMENT */
  const toArray = (v) =>
      Array.isArray(v) ? v : v ? Object.keys(v) : [];

  const latest = (v) =>
      toArray(v).reduce((max, n) => (Number(n) > Number(max) ? n : max), '');

  const [appCommandSettings, setAppCommandSettings] = useState({
    allowInstall: true,
    allowReinstall: true,
    allowDelete: true,
    allowVersionChange: true,
    allowOpen: true,
    allowRestart: true,
    allowClose: true,
  });

  const [editAppCommandSettings, setEditAppCommandSettings] = useState({
    allowInstall: true,
    allowReinstall: true,
    allowDelete: true,
    allowVersionChange: true,
    allowOpen: true,
    allowRestart: true,
    allowClose: true,
  });

  const [systemCommandSettings, setSystemCommandSettings]     = useState({
    allowReboot:true,
    allowReset: true,
    kioskMode: true,
  });

  const [editSystemCommandSettings, setEditSystemCommandSettings]     = useState({
    allowReboot:true,
    allowReset: true,
    kioskMode: true,
  });

  const allowedGeneralSystem = [
    ['Reboot', systemCommandSettings.allowReboot],
    ['Reset', systemCommandSettings.allowReset],
    ['Kiosk Mode', systemCommandSettings.kioskMode]
  ]
      .filter(([_, allowed]) => allowed)
      .map(([label]) => label)
      .join(', ');

  const toggleSystemCommand = (key) => {
    setSystemCommandSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleAppCommand = (key) => {
    setAppCommandSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleEditSystemCommand = (key) => {
    setEditSystemCommandSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleEditAppCommand = (key) => {
    setEditAppCommandSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const allowedGeneralApps = [
    ['Install', appCommandSettings.allowInstall],
    ['Reinstall', appCommandSettings.allowReinstall],
    ['Delete', appCommandSettings.allowDelete],
    ['Version Change', appCommandSettings.allowVersionChange],
  ]
      .filter(([_, allowed]) => allowed)
      .map(([label]) => label)
      .join(', ');

  const allowedControlApps = [
    ['Open', appCommandSettings.allowOpen],
    ['Restart', appCommandSettings.allowRestart],
    ['Close', appCommandSettings.allowClose],
  ]
      .filter(([_, allowed]) => allowed)
      .map(([label]) => label)
      .join(', ');


  const [appsData, setAppsData] = useState([]);
  const [availableApps, setAvailableApps] = useState([]);   // array OR map
  const [selectedApps,  setSelectedApps]  = useState([]);   // [{appId,version}]
  const [editSelectedApps,  setEditSelectedApps]  = useState([]);   // [{appId,version}]

  const selectedIds = new Set(selectedApps.map(r => r.appId));
  const editSelectedIds = new Set(editSelectedApps.map(r => r.appId));

  // remainingApps / editRemainingApps are defined further down, next to groupDevices: the pickers
  // must not offer an agent that does not belong on this group's devices, and the device list is
  // only available there.

  const addSelectedApp = () => {
    if (!remainingApps.length) return;          // nothing left to add
    // console.log("remaining apps", remainingApps)
    // 1) first unused option
    const nextId   = remainingApps[0];

    // 2) full record from the master list (availArray / availableApps)
    const appData  = appsData[nextId];
    if (!appData) return;                       // safety guard

    // 3) append to selectedApps with its latest version
    setSelectedApps(rows => [
      ...rows,
      {
        appId:   nextId,
        version: latest(appData.versions),      // highest numeric code
        name:    appData.name,                  // keep any extra fields
      },
    ]);
    // console.log("selected apps", selectedApps)
  };

  const changeApp = (idx, newId) =>
      setSelectedApps(rows =>
          rows.map((row, i) =>
              i === idx
                  ? {
                    appId: newId,
                    version: latest(appsData[newId].versions),
                    name: appsData[newId].name,
                  }
                  : row
          )
      );

  const changeAppVersion = (idx, newVersion) =>
      setSelectedApps(rows =>
          rows.map((row, i) =>
              i === idx ? { ...row, version: newVersion } : row
          )
      );

  const removeSelectedApp = (idx) =>
      setSelectedApps((rows) => rows.filter((_, i) => i !== idx));

  const editAddSelectedApp = () => {
    if (!editRemainingApps.length) return;          // nothing left to add
    // console.log("remaining apps", editRemainingApps)
    // 1) first unused option
    const nextId   = editRemainingApps[0];

    // 2) full record from the master list (availArray / availableApps)
    const appData  = appsData[nextId];
    if (!appData) return;                       // safety guard

    // 3) append to selectedApps with its latest version
    setEditSelectedApps(rows => [
      ...rows,
      {
        appId:   nextId,
        version: latest(appData.versions),      // highest numeric code
        name:    appData.name,                  // keep any extra fields
      },
    ]);
    // console.log("selected apps", editSelectedApps)
  };

  const editChangeApp = (idx, newId) =>
      setEditSelectedApps(rows =>
          rows.map((row, i) =>
              i === idx
                  ? {
                    appId: newId,
                    version: latest(appsData[newId].versions),
                    name: appsData[newId].name,
                  }
                  : row
          )
      );

  const editChangeAppVersion = (idx, newVersion) =>
      setEditSelectedApps(rows =>
          rows.map((row, i) =>
              i === idx ? { ...row, version: newVersion } : row
          )
      );

  const editRemoveSelectedApp = (idx) =>
      setEditSelectedApps((rows) => rows.filter((_, i) => i !== idx));







  useEffect(() => {
    return () => {
      // Reset relevant states
      setSelectedApps([]);
      setAppCommandSettings({
        allowInstall: true,
        allowReinstall: true,
        allowDelete: true,
        allowVersionChange: true,
        allowStart: true,
        allowRestart: true,
        allowClose: true,
      });
    };
  }, []);


  const [devices, setDevices] = useState({});
  const [newApp, setNewApp] = useState('');

  const [newGroupName,     setNewGroupName]     = useState('')
  const [isAdding,         setIsAdding]         = useState(false)
  const [isEditing,         setIsEditing]         = useState(false)

  // Bulk operation progress state
  const [bulkProgress, setBulkProgress] = useState({
    isOpen: false,
    progress: 0,
    totalDevices: 0,
    completedDevices: 0,
    failedDevices: 0,
    status: 'idle',
    operationName: 'Processing'
  });

  const [groups,           setGroups]           = useState({})

  const { groupId: selectedGroup } = useParams();
  const navigate = useNavigate();

  const [groupSettings,    setGroupSettings]    = useState(DEFAULT_GROUP_SETTINGS)

  const [selectedKey, setSelectedKey] = useState(null)

  useEffect(()=>{
    setSelectedKey(selectedGroup ? encodeURIComponent(selectedGroup) : null)
  },[selectedGroup])


  const [editGroupName,     setEditGroupName]     = useState('')

  useEffect(() => {
    if (!selectedKey || !groups[selectedKey] || !appsData) return;

    const group = groups[selectedKey];
    const groupApps = group.apps || [];
    const settings = group.settings || {};

    // 1. Populate app rows
    const populatedApps = groupApps.map(app => {
      const appMeta = appsData[app.appId];
      const name = appMeta?.name || app.appId;
      const versions = appMeta?.versions;
      const validVersion = versions?.includes?.(app.version)
          || (Array.isArray(versions) ? versions.includes(app.version) : Object.keys(versions || {}).includes(app.version));

      return {
        appId: app.appId,
        version: validVersion ? app.version : latest(versions),
        name,
      };
    });

    setEditSelectedApps(populatedApps);

    // 2. Group name & volume
    setEditGroupName(group.name || '');
    setEditDefaultVolume(settings.volume ?? 80);

    // 3. Extract app-related settings
    const extractedAppCommands = [
      'allowInstall', 'allowReinstall', 'allowDelete', 'allowVersionChange',
      'allowOpen', 'allowRestart', 'allowClose',
    ].reduce((acc, key) => {
      if (key in settings) acc[key] = settings[key];
      return acc;
    }, {});
    setEditAppCommandSettings(prev => ({ ...prev, ...extractedAppCommands }));

    // 4. Extract system-related settings
    const extractedSystemCommands = [
      'allowReboot', 'allowReset', 'kioskMode',
    ].reduce((acc, key) => {
      if (key in settings) acc[key] = settings[key];
      return acc;
    }, {});
    setEditSystemCommandSettings(prev => ({ ...prev, ...extractedSystemCommands }));

  }, [groups, appsData, selectedKey]);





// Fetch groups
  useEffect(() => {
    const groupRef = ref(db, 'mdm/groups')
    return onValue(groupRef, snap => {
      const data = snap.val() || {}
      setGroups(data)
    })
  }, [])

  useEffect(() => {
    const appsRef = ref(db, 'mdm/app_store');
    return onValue(appsRef, snap => {
      const data = snap.val() || {}
      setAppsData(data)
      setAvailableApps(Object.keys(data))
    })
  }, [])

  // Fetch devices
  useEffect(() => {
    const devicesRef = ref(db, 'mdm/devices');
    return onValueThrottled(devicesRef, (snap) => {
      const data = snap.val() || {}
      setDevices(data)
    });
  }, []);

  const groupDevices = Object.values(devices).filter(
      dev => dev?.group === selectedKey
  );

  // Apps this group may be configured with. The store carries the MDM agent for both product
  // lines; installing the wrong one gives the device an APK it cannot run (other signing key, and
  // on Android 12 and older the sharedUserId fails the install outright) and a second agent
  // fighting the real one. So an agent is offered only when every device in the group is on its
  // line - a mixed group simply does not get the agent, which is the correct answer. Third-party
  // apps and the Player are unaffected.
  const groupInstallableApps = availableApps.filter(
      id => isPackageAllowedForDevices(appsData?.[id]?.originalPackageName, groupDevices));

  const remainingApps = groupInstallableApps.filter(app => !selectedIds.has(app));
  const editRemainingApps = groupInstallableApps.filter(app => !editSelectedIds.has(app));

  const totalDevices = groupDevices.length;

  const onlineDevices = groupDevices.filter(dev =>  dev.status === 'online').length;

  const offlineDevices = totalDevices - onlineDevices;

  // Update group settings in the database
  const handleManualUpdate = async () => {
    if (/[.#$\[\]]/.test(editGroupName)) {
      toast.error(`Group name must not include characters: '.', '#', '$', '[', ']'`);
      return;
    }

    if (!selectedKey || !groups[selectedKey]) return;

    setIsEditing(true);

    try {
      const trimmedNewName = editGroupName.trim();
      const newGroupKey = encodeURIComponent(trimmedNewName);
      const oldGroupKey = selectedKey;

      // Check if group name changed
      const groupNameChanged = newGroupKey !== oldGroupKey;

      // If changed, ensure new name doesn't already exist
      if (groupNameChanged && groups[newGroupKey]) {
        toast.error(`A group named "${editGroupName}" already exists.`);
        setIsEditing(false);
        return;
      }

      if (groupNameChanged) {
        setSelectedKey(newGroupKey);
      }

      // Prepare the updated group data
      const updatedData = {
        name: editGroupName,
        settings: {
          ...editAppCommandSettings,
          ...editSystemCommandSettings,
          volume: editDefaultVolume,
        },
        apps: editSelectedApps,
      };

      const updates = {};

      if (groupNameChanged) {
        updates[`mdm/groups/${newGroupKey}`] = updatedData;
        updates[`mdm/groups/${oldGroupKey}`] = null;

        // Update all devices pointing to old group
        Object.entries(devices).forEach(([deviceId, deviceData]) => {
          if (deviceData.group === oldGroupKey) {
            updates[`mdm/devices/${deviceId}/group`] = newGroupKey;
          }
        });
      } else {
        updates[`mdm/groups/${oldGroupKey}`] = updatedData;
      }

      await update(ref(db), updates);

      toast.success(`Group "${editGroupName}" updated successfully.`);
    } catch (err) {
      console.error('Failed to update group:', err);
      toast.error('Failed to update group: ' + err.message);
    } finally {
      setIsEditing(false);
    }
  };


  // Apply settings to all devices in the group
  // const applySettingsToGroup = async () => {
  //   if (!selectedGroup) return;
  //   const groupDevices = Object.keys(devices).filter(deviceId => devices[deviceId].group === selectedGroup);
  //
  //   for (const deviceId of groupDevices) {
  //     const cmdRef = ref(db, `mdm/devices/${deviceId}/command`);
  //
  //     // Install apps
  //     for (const app of groupSettings.appsToInstall || []) {
  //       const appCommand = { command: 'install_app', package: app };
  //       await set(cmdRef, appCommand);
  //       setTimeout(() => set(cmdRef, null), 5000);
  //       toast.success(`Installing app ${app} on device ${deviceId}`);
  //     }
  //
  //     // Apply OTA update
  //     if (groupSettings.otaUpdate.enabled) {
  //       const otaCommand = { command: 'check_ota_update' };
  //       await set(cmdRef, otaCommand);
  //       setTimeout(() => set(cmdRef, null), 5000);
  //       if (groupSettings.otaUpdate.reboot) {
  //         const rebootCommand = { command: 'reboot' };
  //         await set(cmdRef, rebootCommand);
  //         setTimeout(() => set(cmdRef, null), 5000);
  //         toast.success(`OTA update with reboot initiated for device ${deviceId}`);
  //       } else {
  //         toast.success(`OTA update initiated for device ${deviceId}`);
  //       }
  //     }
  //
  //     // Apply screen timeout
  //     const screenTimeoutCommand = { command: 'set_screen_timeout', seconds: groupSettings.screenTimeout };
  //     await set(cmdRef, screenTimeoutCommand);
  //     setTimeout(() => set(cmdRef, null), 5000);
  //     toast.success(`Set screen timeout to ${groupSettings.screenTimeout} seconds on device ${deviceId}`);
  //
  //     // Apply kiosk mode
  //     const kioskModeCommand = { command: groupSettings.kioskMode ? 'enable_kiosk_mode' : 'disable_kiosk_mode' };
  //     await set(cmdRef, kioskModeCommand);
  //     setTimeout(() => set(cmdRef, null), 5000);
  //     toast.success(`${groupSettings.kioskMode ? 'Enabled' : 'Disabled'} kiosk mode on device ${deviceId}`);
  //
  //     // Apply volume level
  //     const volumeCommand = { command: 'set_volume', level: groupSettings.volumeLevel };
  //     await set(cmdRef, volumeCommand);
  //     setTimeout(() => set(cmdRef, null), 5000);
  //     toast.success(`Set volume to ${groupSettings.volumeLevel}% on device ${deviceId}`);
  //
  //     // Apply brightness level
  //     const brightnessCommand = { command: 'set_brightness', level: groupSettings.brightnessLevel };
  //     await set(cmdRef, brightnessCommand);
  //     setTimeout(() => set(cmdRef, null), 5000);
  //     toast.success(`Set brightness to ${groupSettings.brightnessLevel}% on device ${deviceId}`);
  //
  //     // Apply other commands
  //     for (const command of groupSettings.commands || []) {
  //       const commandObj = commandRegistry[Object.keys(commandRegistry).find(cat =>
  //         commandRegistry[cat].commands.some(cmd => cmd.command === command)
  //       )]?.commands.find(cmd => cmd.command === command);
  //       if (commandObj) {
  //         await set(cmdRef, { command: commandObj.command });
  //         setTimeout(() => set(cmdRef, null), 5000);
  //         toast.success(`Command "${commandObj.command}" sent to device ${deviceId}`);
  //       }
  //     }
  //   }
  // };

  const handleManualAdd = async () => {
    if (/[.#$\[\]]/.test(newGroupName)) {
      toast.error(`Group name must not include characters: '.', '#', '$', '[', ']'`);
      return;
    }

    if(!newGroupName) {
      toast.error('Please enter a Group name.');
    }

    if (!newGroupName.trim()) return;
    setIsAdding(true);


    const groupKey = encodeURIComponent(newGroupName.trim());
    const groupRef = ref(db, `mdm/groups/${groupKey}`);

    try {
      // Check if group already exists
      const groupSnap = await get(groupRef);
      if (groupSnap.exists()) {
        toast.error(`Group "${newGroupName}" already exists.`);
        return;
      }

      // Compose group data
      const groupData = {
        name: newGroupName,
        settings: {
          ...appCommandSettings,
          ...systemCommandSettings,
          volume: defaultVolume,
        },
        apps: selectedApps,
      };

      // Save to database
      await set(groupRef, groupData);

      // Reset all creation states
      setNewGroupName('');
      setSelectedApps([]);
      setAppCommandSettings({
        allowInstall: true,
        allowReinstall: true,
        allowDelete: true,
        allowVersionChange: true,
        allowOpen: true,
        allowRestart: true,
        allowClose: true,
      });
      setSystemCommandSettings({
        allowReboot: true,
        allowReset: true,
        kioskMode: true,
      });
      setDefaultVolume(80); // reset to default if needed

      toast.success(`Group "${newGroupName}" created.`);
    } catch (err) {
      console.error('Failed to create group:', err);
      toast.error('Failed to create group.');
    } finally {
      setIsAdding(false);
    }
  };

  const pieData = {
    labels: ['Online', 'Offline'],
    datasets: [
      {
        data: [onlineDevices, offlineDevices],
        backgroundColor: ['#10b981', '#d1d5db'],            // emerald-500, gray-300
        hoverBackgroundColor: ['#059669', '#9ca3af'],       // emerald-600, gray-400
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#D1D5DB',
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#D1D5DB',
        bodyColor: '#D1D5DB',
      },
    },
    maintainAspectRatio: false,
  };

  const groupVolume = groups[selectedKey]?.settings?.volume;

  const audioDiscrepancyCount = Object.values(devices || {}).filter(
      dev =>
          dev?.group === selectedKey &&
          typeof dev.volume_level === 'number' &&
          dev.volume_level !== groupVolume
  ).length;

  const groupApps = groups[selectedKey]?.apps || [];
  const safeKey = pkg => encodeURIComponent(pkg.trim().replace(/\./g, '_'));

  const appDiscrepancyCount = Object.values(devices).filter(device => {
    // Only consider devices from this group
    if (device.group !== selectedKey) return false;

    const installedApps = device.app_list
        ? (Array.isArray(device.app_list)
            ? device.app_list
            : Object.values(device.app_list))
        : null;

    // If app_list is missing or empty, and group has required apps, it's a discrepancy
    if (!installedApps || installedApps.length === 0) {
      return groupApps.length > 0;
    }

    // Check if every required group app is installed correctly
    return groupApps.some(groupApp => {
      const targetKey = safeKey(groupApp.appId);
      const targetVersion = String(groupApp.version);

      const matchedApp = installedApps.find(app =>
          safeKey(app.packageName || '') === targetKey &&
          String(app.appVersion || app.version || '') === targetVersion
      );

      // If no matching app or wrong version, it’s a discrepancy
      return !matchedApp;
    });
  }).length;


  const handleResetAudioDefaults = async () => {
    if (!selectedKey || !groups[selectedKey]) return;

    const defaultVolume = groups[selectedKey].settings?.volume;
    if (defaultVolume == null) {
      toast.error("No default volume defined in group settings.");
      return;
    }

    const deviceIds = Object.entries(devices)
        .filter(([_, d]) => d.group === selectedKey)
        .map(([id]) => id);

    const totalDevices = deviceIds.length;
    if (totalDevices === 0) {
      toast.info("No devices in this group.");
      return;
    }

    const updates = {};
    for (const deviceId of deviceIds) {
      updates[`mdm/devices/${deviceId}/volume_level`] = defaultVolume;
    }

    // Always show progress modal for group operations (more than 1 device)
    const showProgress = totalDevices > 1;

    if (showProgress) {
      setBulkProgress({
        isOpen: true,
        progress: 0,
        totalDevices,
        completedDevices: 0,
        failedDevices: 0,
        status: 'running',
        operationName: 'Resetting Volume'
      });
    }

    try {
      const results = await batchUpdate(db, updates, {
        onProgress: (progress) => {
          if (showProgress) {
            setBulkProgress(prev => ({ ...prev, progress }));
          }
        },
        onBatchComplete: ({ success, pathsInBatch }) => {
          if (showProgress) {
            setBulkProgress(prev => ({
              ...prev,
              completedDevices: prev.completedDevices + (success ? pathsInBatch : 0),
              failedDevices: prev.failedDevices + (success ? 0 : pathsInBatch)
            }));
          }
        }
      });

      const successCount = countDevicesFromPaths(results.success);
      const failCount = countDevicesFromPaths(results.failed);

      if (showProgress) {
        setBulkProgress(prev => ({
          ...prev,
          progress: 100,
          completedDevices: successCount,
          failedDevices: failCount,
          status: failCount > 0 ? 'partial' : 'completed'
        }));
      }

      if (failCount === 0) {
        if (!showProgress) toast.success("All devices' volume levels have been reset to default.");
      } else if (successCount > 0) {
        toast.warning(`Volume reset for ${successCount} devices, ${failCount} failed`);
      } else {
        toast.error("Failed to reset volume levels.");
      }
    } catch (err) {
      if (showProgress) {
        setBulkProgress(prev => ({ ...prev, status: 'failed' }));
      }
      console.error("Error resetting volume levels:", err);
      toast.error("Failed to reset volume levels: " + err.message);
    }
  };

  const handleResetAppDefaults = async () => {
    if (!selectedKey || !groups[selectedKey]) return;

    const groupDevices = Object.entries(devices)
        .filter(([_, d]) => d.group === selectedKey);

    if (groupDevices.length === 0) {
      toast.info("No devices in this group.");
      return;
    }

    const groupApps = groups[selectedKey]?.apps || [];

    if (groupApps.length === 0) {
      toast.info("No apps configured for this group.");
      return;
    }

    const appInfo = groupApps.map(app => {
      const appMeta = appsData?.[app.appId] || {};
      return {
        appId: app.appId,
        originalPackageName: appMeta.originalPackageName?.trim() || app.appId,
        version: String(app.version).trim(),
      };
    });

    // Create installMatrix: all combinations of deviceId + needed apps
    let installMatrix = groupDevices.flatMap(([deviceId]) =>
        appInfo.map(app => ({
          deviceId,
          originalPackageName: app.originalPackageName,
          version: app.version,
        }))
    );

    // Filter out already satisfied installations
    installMatrix = installMatrix.filter(({ deviceId, originalPackageName, version }) => {
      const installedApps = Object.values(devices[deviceId]?.app_list || []);
      return !installedApps.some(app =>
          String(app.packageName).trim() === originalPackageName &&
          String(app.appVersion).trim() === version
      );
    });

    if (installMatrix.length === 0) {
      toast.info("All devices already have the required apps installed.");
      return;
    }

    // Group installMatrix by deviceId
    const commandsByDevice = installMatrix.reduce((acc, { deviceId, originalPackageName, version }) => {
      if (!acc[deviceId]) acc[deviceId] = [];
      acc[deviceId].push(`install:${originalPackageName}:${version}`);
      return acc;
    }, {});

    // Create final command object
    const deviceCommands = Object.entries(commandsByDevice).reduce((acc, [deviceId, commands]) => {
      acc[deviceId] = commands.join('|');
      return acc;
    }, {});

    const updates = {};
    Object.entries(deviceCommands).forEach(([deviceId, command]) => {
      updates[`mdm/devices/${deviceId}/command`] = command;
    });

    const totalDevices = Object.keys(deviceCommands).length;

    // Always show progress modal for group operations (more than 1 device)
    const showProgress = totalDevices > 1;

    if (showProgress) {
      setBulkProgress({
        isOpen: true,
        progress: 0,
        totalDevices,
        completedDevices: 0,
        failedDevices: 0,
        status: 'running',
        operationName: 'Installing Apps'
      });
    }

    try {
      const results = await batchUpdate(db, updates, {
        onProgress: (progress) => {
          if (showProgress) {
            setBulkProgress(prev => ({ ...prev, progress }));
          }
        },
        onBatchComplete: ({ success, pathsInBatch }) => {
          if (showProgress) {
            setBulkProgress(prev => ({
              ...prev,
              completedDevices: prev.completedDevices + (success ? pathsInBatch : 0),
              failedDevices: prev.failedDevices + (success ? 0 : pathsInBatch)
            }));
          }
        }
      });

      const successCount = countDevicesFromPaths(results.success);
      const failCount = countDevicesFromPaths(results.failed);

      if (showProgress) {
        setBulkProgress(prev => ({
          ...prev,
          progress: 100,
          completedDevices: successCount,
          failedDevices: failCount,
          status: failCount > 0 ? 'partial' : 'completed'
        }));
      }

      if (failCount === 0) {
        if (!showProgress) toast.success(`Install commands sent to ${successCount} device${successCount !== 1 ? 's' : ''}.`);
      } else if (successCount > 0) {
        toast.warning(`Install commands sent to ${successCount} devices, ${failCount} failed`);
      } else {
        toast.error('Failed to send app install commands.');
      }
    } catch (err) {
      if (showProgress) {
        setBulkProgress(prev => ({ ...prev, status: 'failed' }));
      }
      console.error('Failed to send app install commands:', err);
      toast.error('Failed to send app install commands: ' + err.message);
    }

    // Update apps_to_watch list
    try {
      const appsToInstall = new Set(
          (groups[selectedKey]?.apps || [])
              .map(app => appsData[app.appId]?.originalPackageName?.trim())
              .filter(Boolean)
      );

      const appsToWatchRef = ref(db, 'mdm/apps_to_watch');
      const snap = await get(appsToWatchRef);
      const existingRaw = snap.exists() ? snap.val() : '';
      const existingList = existingRaw.split(',').map(p => p.trim()).filter(Boolean);

      const newPackages = [...appsToInstall].filter(pkg => !existingList.includes(pkg));
      if (!newPackages.length) return;

      const updatedList = [...existingList, ...newPackages];
      const updatedString = updatedList.join(', ');

      await set(appsToWatchRef, updatedString);
      toast.success(`apps_to_watch updated with ${newPackages.length} new package${newPackages.length > 1 ? 's' : ''}.`);
    } catch (err) {
      console.error('Failed to update apps_to_watch:', err);
      toast.error('Failed to update apps_to_watch list.');
    }

    // const requiredAppIds = Array.from(groupAppMap.keys());
    //
    // const affectedDevices = Object.entries(devices).filter(([_, dev]) => dev.group === selectedKey);
    //
    // const deviceCommands = {};
    // const allWatchApps = new Set();
    //
    // affectedDevices.forEach(([deviceId, deviceData]) => {
    //   const appList = deviceData.app_list || {};
    //   const commands = [];
    //
    //   for (const appId of requiredAppIds) {
    //     const expectedVersion = groupAppMap.get(appId);
    //     const encodedAppId = encodeURIComponent(appId).trim();
    //
    //     const foundApp = Object.entries(appList).find(
    //         ([pkgKey, pkgData]) =>
    //             encodeURIComponent(pkgKey).trim() === encodedAppId &&
    //             String(pkgData?.version) === String(expectedVersion)
    //     );
    //
    //     if (!foundApp) {
    //       commands.push(`install:${appId}:${expectedVersion}`);
    //       allWatchApps.add(appId);
    //     }
    //   }
    //
    //   if (commands.length) {
    //     deviceCommands[deviceId] = commands.join('|');
    //   }
    // });
    //
    // // Prepare Firebase update
    // const updates = {};
    // Object.entries(deviceCommands).forEach(([deviceId, command]) => {
    //   updates[`mdm/devices/${deviceId}/command`] = command;
    // });
    //
    // // Fetch existing apps_to_watch and merge
    // try {
    //   const appsToWatchSnap = await get(ref(db, 'mdm/apps_to_watch'));
    //   const existing = appsToWatchSnap.exists() ? appsToWatchSnap.val().split(',').map(s => s.trim()) : [];
    //   const combined = Array.from(new Set([...existing, ...allWatchApps])).sort();
    //   updates['mdm/apps_to_watch'] = combined.join(', ');
    //
    //   await update(ref(db), updates);
    //   toast.success('App default commands sent to devices.');
    // } catch (err) {
    //   console.error('Error resetting app defaults:', err);
    //   toast.error('Failed to reset apps to default.');
    // }
  };

  const [deleteDeviceCandidate, setDeleteDeviceCandidate] = useState('')

  // Group change history (mdm/group_audit): who moved which devices where, with Revert.
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [revertCandidate, setRevertCandidate] = useState(null);
  const refreshHistory = () => {
    loadGroupHistory(db, 30).then(setHistory).catch((e) => console.error('Failed to load group history:', e));
  };
  useEffect(() => { refreshHistory(); }, []);

  const groupName = (key) => (key ? (groups[key]?.name || decodeURIComponent(key)) : 'No group');
  const describeChange = (e) => {
    const n = e.count || Object.keys(e.changes || {}).length;
    if (e.action === 'revert') return `Reverted a change (${n} device${n === 1 ? '' : 's'} back)`;
    if (e.action === 'delete_group') {
      const from = Object.values(e.changes || {})[0]?.from;
      return `Group "${groupName(from)}" deleted, ${n} device${n === 1 ? '' : 's'} ungrouped`;
    }
    if (!e.target) return `${n} device${n === 1 ? '' : 's'} removed from their group`;
    return `${n} device${n === 1 ? '' : 's'} moved to "${groupName(e.target)}"`;
  };
  const fromSummary = (e) => {
    const counts = {};
    Object.values(e.changes || {}).forEach(({ from }) => { counts[from || ''] = (counts[from || ''] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${groupName(k)} (${n})`).join(', ');
  };
  const doRevert = async (entry) => {
    try {
      const { restored, skipped } = await revertGroupChange(db, entry.id);
      toast.success(`Reverted: ${restored} device(s) back in their previous group` + (skipped ? ` (${skipped} changed again since, left as is)` : ''));
    } catch (e) {
      toast.error('Revert failed: ' + e.message);
    } finally {
      setRevertCandidate(null);
      refreshHistory();
    }
  };
  const [deleteGroupCandidate, setDeleteGroupCandidate] = useState('')

  const handleRemoveDevice = async (deviceId) => {
    try {
      const deviceRef = ref(db, `mdm/devices/${deviceId}`);
      const snap = await get(deviceRef);
      const deviceData = snap.val();

      if (!deviceData) {
        toast.error(`Device ${deviceId} not found.`);
        return;
      }

      await applyGroupChange(db, [{ id: deviceId, from: deviceData.group || '' }], '', { source: 'groups' });
      refreshHistory();

      toast.success(`Device ${deviceId} removed from group.`);
    } catch (err) {
      console.error('Failed to remove group from device:', err);
      toast.error('Failed to remove device from group.');
    }
  };

  const deleteEmptyGroup = async (groupKey) => {
    if (!groupKey || !groups[groupKey]) {
      toast.error('Invalid group.');
      return;
    }

    try {
      await remove(ref(db, `mdm/groups/${groupKey}`));
      toast.success(`Group "${groups[groupKey].name}" deleted.`);
      if (selectedKey === groupKey) setSelectedKey(null);
    } catch (err) {
      console.error('Failed to delete group:', err);
      toast.error('Could not delete group: ' + err.message);
    }
  };

  const deleteGroupAndUngroupDevices = async (groupKey) => {
    if (!groupKey || !groups[groupKey]) {
      toast.error('Invalid group.');
      return;
    }

    try {
      // 1. Remove the group from each device, recorded in the group history so it can be reverted.
      const moves = Object.entries(devices)
          .filter(([, dev]) => dev.group === groupKey)
          .map(([deviceId]) => ({ id: deviceId, from: groupKey }));
      if (moves.length) {
        await applyGroupChange(db, moves, '', { source: 'groups', action: 'delete_group' });
      }

      // 2. Delete group itself
      await update(ref(db), { [`mdm/groups/${groupKey}`]: null });
      refreshHistory();
      toast.success(`Group "${groups[groupKey].name}" and its device assignments deleted.`);
      if (selectedKey === groupKey) setSelectedKey(null);
    } catch (err) {
      console.error('Failed to delete group and unassign devices:', err);
      toast.error('Could not delete group: ' + err.message);
    }
  };


  return (
      <div className="page-shell">
        <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
        />

        <BulkOperationProgress
            isOpen={bulkProgress.isOpen}
            progress={bulkProgress.progress}
            totalDevices={bulkProgress.totalDevices}
            completedDevices={bulkProgress.completedDevices}
            failedDevices={bulkProgress.failedDevices}
            status={bulkProgress.status}
            operationName={bulkProgress.operationName}
            onClose={() => setBulkProgress(prev => ({ ...prev, isOpen: false, status: 'idle' }))}
        />

        <div className="flex items-center gap-3 mb-6">
          <div className="brand-icon-badge"><FiUsers /></div>
          <h1 className="text-xl font-semibold text-white">Groups Management</h1>
        </div>

        <div
            className="card-chrome mb-4 w-full">
          <header className="flex items-center gap-3 mb-4">
            <div className="brand-icon-badge"><FiPlus /></div>
            <h2 className="text-sm font-semibold text-white">Create New Group</h2>
          </header>

          <div className="mb-6">
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Group Name</label>
            <input
                type="text"
                placeholder="e.g. Office Devices"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="input-field"
            />
          </div>

          <section className="space-y-4">
            {/* 1 — System Control */}
            <details className="card-inset p-4 group">
              <summary className="flex items-center justify-between cursor-pointer select-none text-white">
                {/* Left Side: Icon + Label */}
                <span className="flex items-center gap-2 font-medium">
                  <FiSettings className="text-brand-400"/>
                  System&nbsp;Control
                </span>

                {/* Right Side: Status + Chevron together */}
                <span className="flex items-center gap-3">
                  {!!allowedGeneralSystem && (
                      <span className="bg-white/[0.05] border border-white/[0.08] px-2 py-0.5 rounded-md text-gray-300 text-xs font-medium">
                        General: {allowedGeneralSystem}
                      </span>
                  )}
                  <FiChevronDown className="transition-transform duration-200 group-open:rotate-180"/>
                </span>
              </summary>

              {/*<div className="w-full md:w-1/2 space-y-3 mt-4">*/}
              {/*  <div className="mt-4 space-y-4 pl-1">*/}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 relative">


                    <div className="space-y-3">
                      <h4 className="text-sm text-gray-400 font-semibold mb-2">General Settings</h4>
                      <div className="space-y-2">
                        {[
                          ['Allow Reboot', 'allowReboot'],
                          ['Allow Reset', 'allowReset'],
                          ['Allow Kiosk Mode', 'kioskMode'],
                        ].map(([label, key]) => (
                            <label
                                key={key}
                                className="flex items-center justify-between px-2 py-1 rounded hover:bg-white/[0.04] transition-colors"
                            >
                              <span className="text-sm text-gray-300 mr-2">{label}</span>
                              <button
                                  type="button"
                                  onClick={() => toggleSystemCommand(key)}
                                  className={`w-10 h-5 flex items-center rounded-full px-0.5 transition-colors duration-300
              ${systemCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}
            `}
                              >
            <span
                className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300
                ${systemCommandSettings[key] ? 'translate-x-5' : ''}
              `}
            />
                              </button>
                            </label>
                        ))}
                      </div>
                    </div>
                  </div>
            </details>

            {/* 2 — Audio Settings */}
            <details className="card-inset p-4 group">
              <summary className="flex items-center justify-between cursor-pointer select-none text-white">
                {/* Left Side: Icon + Label */}
                <span className="flex items-center gap-2 font-medium">
                  <FiVolume2 className="text-brand-400"/>
                  Audio Settings
                </span>

                {/* Right Side: Status + Chevron together */}
                <span className="flex items-center gap-3">
                    <span className="bg-white/[0.05] border border-white/[0.08] px-2 py-0.5 rounded-md text-gray-300 text-xs font-medium">
                      Volume: {defaultVolume}
                    </span>
                  <FiChevronDown className="transition-transform duration-200 group-open:rotate-180"/>
                </span>
              </summary>

              <div className="mt-4 pl-1">
                <CustomSlider
                    label="Default Volume"
                    value={defaultVolume}
                    onChange={setDefaultVolume}
                />
              </div>
            </details>

            {/*  /!* 3 — Display Adjustments *!/*/}
            {/*  <details className="card-inset p-4 group">*/}
            {/*    <summary className="flex items-center justify-between cursor-pointer select-none text-white">*/}
            {/*<span className="flex items-center gap-2">*/}
            {/*  <FiMonitor className="text-brand-400"/> Display Adjustments*/}
            {/*</span>*/}
            {/*      <FiChevronDown className="transition-transform duration-200 group-open:rotate-180"/>*/}
            {/*    </summary>*/}

            {/*    <div className="mt-4 space-y-4 pl-1">*/}
            {/*      <div>*/}
            {/*        <label className="block text-sm mb-2 text-gray-300">Default Brightness ({defaultBrightness}%)</label>*/}
            {/*        <input*/}
            {/*            type="range"*/}
            {/*            min="0"*/}
            {/*            max="100"*/}
            {/*            value={defaultBrightness}*/}
            {/*            onChange={e => setDefaultBrightness(+e.target.value)}*/}
            {/*            className="w-full accent-blue-500"*/}
            {/*        />*/}
            {/*      </div>*/}

            {/*      <div>*/}
            {/*        <label className="block text-sm mb-2 text-gray-300">Screen Timeout ({screenTimeout}s)</label>*/}
            {/*        <input*/}
            {/*            type="number"*/}
            {/*            min="10"*/}
            {/*            value={screenTimeout}*/}
            {/*            onChange={e => setScreenTimeout(+e.target.value)}*/}
            {/*            className="h-10 w-full bg-gray-700 border border-gray-600 rounded-lg px-3 text-white"*/}
            {/*        />*/}
            {/*      </div>*/}
            {/*    </div>*/}
            {/*  </details>*/}

            {/* 4 — App Management */}
            <details className="card-inset p-4 group">
              <summary
                  className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-0 cursor-pointer select-none text-white">
                {/* Left side: Title with icon */}
                <span className="flex items-center gap-2 font-medium">
    <FiPackage className="text-brand-400"/>
    App&nbsp;Management
  </span>

                {/* Right side: Info tags + chevron */}
                <span className="flex flex-wrap items-center gap-2 text-sm text-gray-400 lg:ml-4 mt-1 lg:mt-0">
    {!!selectedApps.length && (
        <span className="bg-white/[0.05] border border-white/[0.08] px-2 py-0.5 rounded-md text-gray-300 text-xs font-medium">
        {selectedApps.length} app{selectedApps.length !== 1 ? 's' : ''}
      </span>
    )}

                  {allowedGeneralApps && (
                      <span className="bg-white/[0.05] border border-white/[0.08] px-2 py-0.5 rounded-md text-gray-300 text-xs font-medium">
        General: {allowedGeneralApps}
      </span>
                  )}

                  {allowedControlApps && (
                      <span className="bg-white/[0.05] border border-white/[0.08] px-2 py-0.5 rounded-md text-gray-300 text-xs font-medium">
        Control: {allowedControlApps}
      </span>
                  )}

                  <FiChevronDown className="ml-2 transition-transform duration-200 group-open:rotate-180"/>
  </span>
              </summary>

              {/* rows */}
              <div className="mt-4 space-y-4 pl-1">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 relative">
                  {/* General App Settings */}
                  <div className="space-y-3">
                    <h4 className="text-sm text-gray-400 font-semibold mb-2">General App Settings</h4>
                    <div className="space-y-2">
                      {[
                        ['Allow Install', 'allowInstall'],
                        ['Allow Reinstall', 'allowReinstall'],
                        ['Allow Delete', 'allowDelete'],
                        ['Allow Version Change', 'allowVersionChange'],
                      ].map(([label, key]) => (
                          <label
                              key={key}
                              className="flex justify-between items-center px-2 py-1 rounded hover:bg-white/[0.04] transition-colors min-w-0"
                          >
                            <span className="text-sm text-gray-300 mr-2 truncate">{label}</span>
                            <button
                                type="button"
                                onClick={() => toggleAppCommand(key)}
                                className={`w-10 h-5 flex-shrink-0 flex items-center rounded-full px-0.5 transition-colors duration-300
      ${appCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}
    `}
                            >
    <span
        className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300
        ${appCommandSettings[key] ? 'translate-x-5' : ''}
      `}
    />
                            </button>
                          </label>
                      ))}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-gray-600/50"/>

                  {/* Live App Controls */}
                  <div className="space-y-3">
                    <h4 className="text-sm text-gray-400 font-semibold mb-2">Live App Controls</h4>
                    <div className="space-y-2">
                      {[
                        ['Allow Open', 'allowOpen'],
                        ['Allow Restart', 'allowRestart'],
                        ['Allow Close', 'allowClose'],
                      ].map(([label, key]) => (
                          <label
                              key={key}
                              className="flex justify-between items-center px-2 py-1 rounded hover:bg-white/[0.04] transition-colors min-w-0"
                          >
                            <span className="text-sm text-gray-300 mr-2 truncate">{label}</span>
                            <button
                                type="button"
                                onClick={() => toggleAppCommand(key)}
                                className={`w-10 h-5 flex-shrink-0 flex items-center rounded-full px-0.5 transition-colors duration-300
      ${appCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}
    `}
                            >
    <span
        className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300
        ${appCommandSettings[key] ? 'translate-x-5' : ''}
      `}
    />
                            </button>
                          </label>
                      ))}
                    </div>
                  </div>
                </div>

                <hr className="my-6 border-t border-gray-700/50"/>
                <h4 className="text-sm text-gray-400 font-semibold mb-2">Default Apps</h4>
                {selectedApps.map((row, idx) => {

                  const currentApp = appsData[row.appId]
                  // Extract versions safely
                  const versionArr = Array.isArray(currentApp?.versions)
                      ? currentApp.versions
                      : currentApp?.versions
                          ? Object.keys(currentApp.versions)
                          : [];
                  const singleVersion = versionArr.length === 1;

                  return (
                      <div key={idx} className="flex flex-col md:flex-row gap-4 w-full">
                        {/* App Name Field */}
                        <button
                            type="button"
                            onClick={() => removeSelectedApp(idx)}
                            className="mt-[30px] text-red-500 hover:text-red-400"
                            title="Remove app"
                        >
                          <FiTrash2 className="w-5 h-5"/>
                        </button>
                        <div className="flex-1">
                          <CustomDropdown
                              label="App Name"
                              value={row.appId}
                              onChange={newValue => changeApp(idx, newValue)}
                              options={[
                                {
                                  value: row.appId,
                                  label: appsData[row.appId]?.name || row.appId,
                                },
                                ...remainingApps.map(id => ({
                                  value: id,
                                  label: appsData[id]?.name || id,
                                })),
                              ]}
                          />
                        </div>

                        {/* App Version Field */}
                        <div className="flex-1">
                          <CustomDropdown
                              label="App Version"
                              value={row.version}
                              onChange={v => changeAppVersion(idx, v)}
                              options={versionArr.map(v => ({value: v, label: v}))}
                              disabled={singleVersion}
                          />
                        </div>
                      </div>
                  )
                })}


                {/* ─── Add‑row area ─── */}
                {(() => {
                  const hasAnyApps = availableApps.length > 0;      // the entire store
                  const hasRemaining = remainingApps.length > 0;   // not yet selected
                  const hasSelected = selectedApps.length > 0;

                  // 1) choose default / add more
                  if (hasRemaining) {
                    return (
                        <button
                            type="button"
                            onClick={addSelectedApp}
                            className="flex items-center gap-2 text-brand-400 hover:text-brand text-sm"
                        >
                          <FiPlus/>
                          {hasSelected ? 'Add another app' : 'Select default app'}
                        </button>
                    );
                  }

                  // 2) store had apps, but none left
                  if (hasAnyApps) {
                    return (
                        <p className="text-gray-400 text-sm">No more apps remaining.</p>
                    );
                  }

                  // 3) store completely empty
                  return (
                      <p className="text-gray-400 text-sm">App store is empty.</p>
                  );
                })()}
              </div>
            </details>

          </section>

          {/* Add Group Button */
          }
          <button
              onClick={handleManualAdd}
              disabled={isAdding}
              className="btn-primary mt-4"
          >
            {isAdding ? 'Adding…' : 'Add Group'}
          </button>
        </div>

        {/*List groups*/}
        <div className="flex flex-wrap gap-4 mb-6">
          {Object.entries(groups).length > 0 ? (
              Object.entries(groups).map(([id, group]) => {
                // decode the key back into a human name
                const rawName = decodeURIComponent(id);
                // if you used dashes in the key, convert them back to spaces
                // const displayName = rawName.replace(/-/g, ' ');
                const displayName = decodeURIComponent(id).replace(/-/g, ' ');

                // count how many devices belong to this group
                const deviceCount = Object.values(devices).filter(
                    (d) => d.group === id
                ).length;

                const isSelected = selectedKey === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => navigate(`/groups/${displayName}`)}
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            isSelected
                                ? 'brand-pill'
                                : 'bg-white/[0.02] border-white/[0.08] text-gray-300 hover:bg-white/[0.05] hover:border-white/[0.15]'
                        }`}
                    >
                        <span>{displayName}</span>
                        <span className={`text-xs ${isSelected ? 'text-brand-400' : 'text-gray-500'}`}>
                          {deviceCount} {deviceCount === 1 ? 'device' : 'devices'}
                        </span>
                    </button>
                );
              })
          ) : (
              <p className="text-gray-400">No groups available.</p>
          )}
        </div>

        {/* Group change history - every move / removal is recorded and can be reverted. */}
        <div className="card-chrome w-full mb-6">
          <button
              type="button"
              onClick={() => { setHistoryOpen((o) => !o); if (!historyOpen) refreshHistory(); }}
              className="w-full flex items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold text-white flex items-center gap-2">
              <FiClock className="text-brand-400"/> Group change history
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              {history.length ? `${history.length} recent` : 'No changes recorded yet'}
              <FiChevronDown className={`transition-transform ${historyOpen ? 'rotate-180' : ''}`}/>
            </span>
          </button>
          {historyOpen && (
              <div className="mt-4 space-y-2 max-h-96 overflow-y-auto pr-1">
                {history.length === 0 && (
                    <p className="text-sm text-gray-500">Changes made from now on appear here and can be reverted.</p>
                )}
                {history.map((e) => (
                    <div key={e.id} className="card-inset p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{describeChange(e)}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {new Date(e.at).toLocaleString()} · {e.by}
                          {e.action !== 'revert' && fromSummary(e) ? ` · from ${fromSummary(e)}` : ''}
                        </p>
                      </div>
                      {e.action !== 'revert' && (
                          e.revertedAt ? (
                              <span className="text-xs text-gray-500 whitespace-nowrap">Reverted</span>
                          ) : (
                              <button
                                  onClick={() => setRevertCandidate(e)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.04] border border-white/[0.08] text-gray-200 hover:bg-white/[0.08] whitespace-nowrap"
                              >
                                <FiRotateCcw/> Revert
                              </button>
                          )
                      )}
                    </div>
                ))}
              </div>
          )}
        </div>

        {revertCandidate && (
            <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                 onClick={() => setRevertCandidate(null)}>
              <div className="card-chrome w-full max-w-md" onClick={(ev) => ev.stopPropagation()}>
                <h3 className="text-lg font-semibold text-white mb-2">Revert this change?</h3>
                <p className="text-sm text-gray-400 mb-1">{describeChange(revertCandidate)}</p>
                <p className="text-sm text-gray-400 mb-5">
                  Each device goes back to the group it had before ({fromSummary(revertCandidate)}). Devices whose group
                  was changed again after this are left as they are.
                </p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setRevertCandidate(null)} className="btn-secondary">Cancel</button>
                  <button onClick={() => doRevert(revertCandidate)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand text-white hover:opacity-90">
                    Revert
                  </button>
                </div>
              </div>
            </div>
        )}
        {
            (selectedGroup && groups[selectedKey]) && (
                <div>

                  {/* Group settings panel */}
                  <div className="card-chrome">
                    <section className="space-y-4">
                      <header className="flex items-center gap-3 mb-4">
                        <div className="brand-icon-badge"><FiUsers /></div>
                        <h2 className="text-sm font-semibold text-white">
                          Group: <span className="font-bold">{decodeURIComponent(selectedKey).replace(/-/g, ' ')}</span>
                        </h2>
                      </header>

                      {!!Object.values(devices).filter((d) => d.group === selectedKey).length && (
                          <div className="card-chrome w-full">
                            <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                              <FiPieChart className="text-brand-400"/>
                              Device Status Breakdown
                            </h3>

                            <div className="flex flex-col lg:flex-row gap-6">
                              {/* ─── Left: Pie Chart ─── */}
                              <div className="flex-1">
                                <div className="h-64">
                                  <Pie data={pieData} options={pieOptions}/>
                                </div>
                              </div>

                              {/* ─── Right: Discrepancy Panel ─── */}
                              <div className="flex-1 space-y-6">
                                {/* Audio Discrepancy */}
                                <div
                                    className="card-inset p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                  {/* Left: Info */}
                                  <div className="text-gray-300 space-y-1">
                                    <h4 className="text-base font-semibold text-white">Audio Setting Discrepancy</h4>
                                    <p className="text-sm">
                                      Devices out of sync:{' '}
                                      <span
                                          className="font-bold text-brand-400 text-base">{audioDiscrepancyCount}</span>
                                    </p>
                                  </div>
                                  {audioDiscrepancyCount > 0 && (
                                      <button
                                          onClick={handleResetAudioDefaults}
                                          className="btn-secondary"
                                      >
                                        Return to Default
                                      </button>
                                  )}
                                </div>

                                {/* App Discrepancy */}
                                <div
                                    className="card-inset p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                                >
                                  {/* Left: Info */}
                                  <div className="text-gray-300 space-y-1">
                                    <h4 className="text-base font-semibold text-white">App Setting Discrepancy</h4>
                                    <p className="text-sm">
                                      Devices out of sync:{' '}
                                      <span className="font-bold text-brand-400 text-base">{appDiscrepancyCount}</span>
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Note: App installation is not instant and may take a few minutes to reflect on all
                                      devices.
                                    </p>
                                  </div>

                                  {/* Right: Button */}
                                  {appDiscrepancyCount > 0 && (
                                      <button
                                          onClick={handleResetAppDefaults}
                                          className="btn-secondary"
                                      >
                                        Return to Default
                                      </button>
                                  )}
                                </div>

                              </div>
                            </div>
                          </div>
                      )}

                      <div className="card-chrome w-full">
                        <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                          <FiHardDrive className="text-brand-400"/>
                          Devices
                        </h3>
                        {!!Object.values(devices).filter((d) => d.group === selectedKey).length && (
                            <div className="flex flex-wrap gap-3 mb-6">
                              {Object.keys(devices).map(deviceId => {
                                const isInGroup = devices[deviceId].group === selectedKey;
                                if (!isInGroup) return null;

                                return (
                                    <div className="relative inline-flex items-center group" key={deviceId}>
                                      {/* Delete button revealed when the chip slides right on hover. */}
                                      <button
                                          onClick={() => setDeleteDeviceCandidate(deviceId)}
                                          className="absolute left-0 top-0 h-9 w-10 bg-red-500/20 text-red-300 flex items-center justify-center z-10 rounded-l-lg transition-colors hover:bg-red-500/30"
                                          title="Remove device"
                                      >
                                        <FiTrash2 size={16}/>
                                      </button>

                                      {/* Device ID chip — neutral surface with a subtle border, in
                                          line with the rest of the dashboard's pill aesthetic. */}
                                      <div
                                          className="relative z-20 flex items-center h-9 px-3.5 bg-white/[0.04] border border-white/[0.08] text-gray-200 text-xs font-mono shadow-sm transition-transform duration-300 transform group-hover:translate-x-10 rounded-lg group-hover:rounded-l-none"
                                      >
                                        {deviceId}
                                      </div>
                                    </div>
                                );
                            })}
                          </div>
                        )}
                        <button
                            onClick={() => navigate('/devices/?group=.nogroup.')}
                            className="btn-primary"
                        >
                          <FiPlus/> Add Devices
                        </button>
                      </div>
                      <div className="card-chrome w-full">
                        <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                          <FiLayers className="text-brand-400"/>
                          Info&nbsp;
                        </h3>

                        <div className="mt-4 pl-1">
                          <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">Group Name</label>
                          <input
                              type="text"
                              placeholder="e.g. Office Devices"
                              value={editGroupName}
                              onChange={(e) => setEditGroupName(e.target.value)}
                              className="input-field"
                          />
                        </div>
                        </div>
                        {/* 1 — System Control */}
                        <div className="card-chrome w-full">
                          <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <FiSettings className="text-brand-400"/>
                            System&nbsp;Control
                          </h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 relative">
                            <div className="space-y-3 ">
                              <h4 className="text-sm text-gray-400 font-semibold mb-2">General Settings</h4>
                              <div className="space-y-2">
                                {[
                                  ['Allow Reboot', 'allowReboot'],
                                  ['Allow Reset', 'allowReset'],
                                  ['Allow Kiosk Mode', 'kioskMode'],
                                ].map(([label, key]) => (
                                    <label
                                        key={key}
                                        className="flex items-center justify-between px-2 py-1 rounded hover:bg-white/[0.04] transition-colors"
                                    >
                                      <span className="text-sm text-gray-300 mr-2">{label}</span>
                                      <button
                                          type="button"
                                          onClick={() => toggleEditSystemCommand(key)}
                                          className={`w-10 h-5 flex items-center rounded-full px-0.5 transition-colors duration-300 ${editSystemCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}`}
                                      >
                                        <span
                                            className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300 ${editSystemCommandSettings[key] ? 'translate-x-5' : ''}`}
                                        />
                                      </button>
                                    </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                      {/* 2 — Audio Settings */}
                        <div className="card-chrome w-full">
                          <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <FiVolume2 className="text-brand-400"/>
                            Audio&nbsp;Settings
                          </h3>

                          <div className="mt-4 pl-1">
                            <CustomSlider
                                label="Default Volume"
                                value={editDefaultVolume}
                                onChange={setEditDefaultVolume}
                            />
                          </div>
                        </div>

                        {/* 4 — App Management */}
                        <div className="card-chrome w-full">
                          <h3 className="text-xl font-semibold flex items-center gap-2 mb-6">
                            <FiPackage className="text-brand-400"/>
                            App&nbsp;Management
                          </h3>

                          {/* rows */}
                          <div className="mt-4 space-y-4 pl-1">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 relative">
                              {/* General App Settings */}
                              <div className="space-y-3">
                                <h4 className="text-sm text-gray-400 font-semibold mb-2">General App Settings</h4>
                                <div className="space-y-2">
                                  {[
                                    ['Allow Install', 'allowInstall'],
                                    ['Allow Reinstall', 'allowReinstall'],
                                    ['Allow Delete', 'allowDelete'],
                                    ['Allow Version Change', 'allowVersionChange'],
                                  ].map(([label, key]) => (
                                      <label
                                          key={key}
                                          className="flex items-center justify-between px-2 py-1 rounded hover:bg-white/[0.04] transition-colors"
                                      >
                                        <span className="text-sm text-gray-300 mr-2">{label}</span>
                                        <button
                                            type="button"
                                            onClick={() => toggleEditAppCommand(key)}
                                            className={`w-10 h-5 flex items-center rounded-full px-0.5 transition-colors duration-300
              ${editAppCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}
            `}
                                        >
            <span
                className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300
                ${editAppCommandSettings[key] ? 'translate-x-5' : ''}
              `}
            />
                                        </button>
                                      </label>
                                  ))}
                                </div>
                              </div>

                              {/* Divider */}
                              <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-gray-600/50"/>

                              {/* Live App Controls */}
                              <div className="space-y-3">
                                <h4 className="text-sm text-gray-400 font-semibold mb-2">Live App Controls</h4>
                                <div className="space-y-2">
                                  {[
                                    ['Allow Open', 'allowOpen'],
                                    ['Allow Restart', 'allowRestart'],
                                    ['Allow Close', 'allowClose'],
                                  ].map(([label, key]) => (
                                      <label
                                          key={key}
                                          className="flex items-center justify-between px-2 py-1 rounded hover:bg-white/[0.04] transition-colors"
                                      >
                                        <span className="text-sm text-gray-300 mr-2">{label}</span>
                                        <button
                                            type="button"
                                            onClick={() => toggleEditAppCommand(key)}
                                            className={`w-10 h-5 flex items-center rounded-full px-0.5 transition-colors duration-300
              ${editAppCommandSettings[key] ? 'bg-brand' : 'bg-white/[0.1]'}
            `}
                                        >
            <span
                className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300
                ${editAppCommandSettings[key] ? 'translate-x-5' : ''}
              `}
            />
                                        </button>
                                      </label>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <hr className="my-6 border-t border-gray-700/50"/>
                            <h4 className="text-sm text-gray-400 font-semibold mb-2">Default Apps</h4>
                            {editSelectedApps.map((row, idx) => {
                              const currentApp = appsData[row.appId]
                              // Extract versions safely
                              const versionArr = Array.isArray(currentApp?.versions)
                                  ? currentApp.versions
                                  : currentApp?.versions
                                      ? Object.keys(currentApp.versions)
                                      : [];
                              const singleVersion = versionArr.length === 1;

                              return (
                                  <div key={idx} className="flex flex-col md:flex-row gap-4 w-full">
                                    {/* App Name Field */}
                                    <button
                                        type="button"
                                        onClick={() => editRemoveSelectedApp(idx)}
                                        className="mt-[30px] text-red-500 hover:text-red-400"
                                        title="Remove app"
                                    >
                                      <FiTrash2 className="w-5 h-5"/>
                                    </button>
                                    <div className="flex-1">
                                      <CustomDropdown
                                          label="App Name"
                                          value={row.appId}
                                          onChange={newValue => editChangeApp(idx, newValue)}
                                          options={[
                                            {
                                              value: row.appId,
                                              label: appsData[row.appId]?.name || row.appId,
                                            },
                                            ...editRemainingApps.map(id => ({
                                              value: id,
                                              label: appsData[id]?.name || id,
                                            })),
                                          ]}
                                      />
                                    </div>

                                    {/* App Version Field */}
                                    <div className="flex-1">
                                      <CustomDropdown
                                          label="App Version"
                                          value={row.version}
                                          onChange={v => editChangeAppVersion(idx, v)}
                                          options={versionArr.map(v => ({ value: v, label: v }))}
                                          disabled={singleVersion}
                                      />
                                    </div>
                                  </div>
                              )
                            })}


                            {/* ─── Add‑row area ─── */}
                            {(() => {
                              const hasAnyApps = availableApps.length > 0;      // the entire store
                              const hasRemaining = editRemainingApps.length > 0;   // not yet selected
                              const hasSelected = editSelectedApps.length > 0;

                              // 1) choose default / add more
                              if (hasRemaining) {
                                return (
                                    <button
                                        type="button"
                                        onClick={editAddSelectedApp}
                                        className="flex items-center gap-2 text-brand-400 hover:text-brand text-sm"
                                    >
                                      <FiPlus/>
                                      {hasSelected ? 'Add another app' : 'Select default app'}
                                    </button>
                                );
                              }

                              // 2) store had apps, but none left
                              if (hasAnyApps) {
                                return (
                                    <p className="text-gray-400 text-sm">No more apps remaining.</p>
                                );
                              }

                              // 3) store completely empty
                              return (
                                  <p className="text-gray-400 text-sm">App store is empty.</p>
                              );
                            })()}
                          </div>
                        </div>

                    {/* Save and Apply Buttons */}
                      <div className="flex justify-between flex-row">
                        <button
                            onClick={handleManualUpdate}
                            className="btn-primary"
                        >
                          <FiSettings/> {isEditing ? 'Adding...' : 'Update Group'}
                        </button>
                        <button
                            onClick={()=>{setDeleteGroupCandidate({key:selectedKey, name: selectedGroup})}}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                        >
                          <FiTrash2/> Delete Group
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
            )}
        {deleteDeviceCandidate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="card-chrome w-full max-w-md p-0">
                <header className="px-5 py-4 border-b border-white/[0.06]">
                  <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                    <FiTrash2 className="text-red-400" /> Remove device
                  </h3>
                </header>
                <div className="px-5 py-4">
                  <p className="text-sm text-gray-300">
                    Remove device{' '}
                    <span className="text-white font-semibold font-mono">{deleteDeviceCandidate}</span>{' '}
                    from group{' '}
                    <span className="text-white font-semibold">{selectedGroup}</span>?
                  </p>
                </div>
                <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
                  <button onClick={() => setDeleteDeviceCandidate('')} className="btn-secondary">Cancel</button>
                  <button
                    onClick={async () => {
                      const device = deleteDeviceCandidate;
                      setDeleteDeviceCandidate('');
                      await handleRemoveDevice(device);
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                  >
                    <FiTrash2 /> Remove
                  </button>
                </footer>
              </div>
            </div>
        )}

        {deleteGroupCandidate && (() => {
          const deviceCount = Object.values(devices).filter(d => d.group === selectedKey).length;
          const hasDevices = deviceCount > 0;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="card-chrome w-full max-w-md p-0">
                <header className="px-5 py-4 border-b border-white/[0.06]">
                  <h3 className="inline-flex items-center gap-2 text-base font-semibold text-white">
                    <FiTrash2 className="text-red-400" /> Delete group
                  </h3>
                </header>
                <div className="px-5 py-4 space-y-2">
                  <p className="text-sm text-gray-300">
                    Delete group{' '}
                    <span className="text-white font-semibold">{deleteGroupCandidate.name}</span>?
                  </p>
                  {hasDevices && (
                    <p className="text-xs text-gray-500">
                      <span className="text-gray-300 font-medium">{deviceCount}</span>
                      {' '}device{deviceCount !== 1 ? 's' : ''} currently belong{deviceCount === 1 ? 's' : ''} to this group —
                      they will be ungrouped.
                    </p>
                  )}
                </div>
                <footer className="px-5 py-4 border-t border-white/[0.06] flex justify-end gap-2">
                  <button onClick={() => setDeleteGroupCandidate('')} className="btn-secondary">Cancel</button>
                  <button
                    onClick={async () => {
                      const group = deleteGroupCandidate;
                      setDeleteGroupCandidate('');
                      if (hasDevices) {
                        await deleteGroupAndUngroupDevices(group.key);
                      } else {
                        await deleteEmptyGroup(group.key);
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors"
                  >
                    <FiTrash2 /> {hasDevices ? 'Ungroup & Delete' : 'Delete'}
                  </button>
                </footer>
              </div>
            </div>
          );
        })()}
      </div>
  );
}